/**
 * EditMemberSheet — edit one adult on the household.
 *
 * One sheet, two views (the same internal-view pattern `AddMemberSheet` uses):
 *
 *   detail — identity block, the member's current role as a tappable row, a
 *            read-only list of what that role grants, and "Delete member".
 *   role   — a card-per-role picker showing each role's permissions, with a
 *            back arrow and Save.
 *
 * Permission lists come from the server's `roleCatalog`, never a local copy of
 * the matrix, so this screen cannot promise a capability the server won't
 * enforce.
 *
 * Primary Guardian is a GRANT, not a handover — a household can have several
 * co-equal primary guardians, so promoting someone leaves the viewer's own role
 * untouched. That card only renders for viewers holding MANAGE_PRIMARY_GUARDIAN.
 *
 * The one hard rule: a household must keep at least one primary guardian, so
 * demoting the last one is blocked here (with the reason shown inline) and again
 * on the server, which is the real enforcement.
 */

import { Button } from '@ordo/ui/button'
import { useDestructiveConfirm } from '@ordo/ui/destructive-confirm'
import { Label } from '@ordo/ui/label'
import { RadioGroup, RadioGroupItem } from '@ordo/ui/radio-group'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@ordo/ui/sheet'
import { toast } from '@ordo/ui/sonner'
import { ArrowLeft, Check, ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  type CoOwnerRow,
  type HouseholdPermission,
  type HouseholdRole,
  type ListCoOwnersResponse,
  useRemoveAccountCoOwnerMutation,
  useUpdateAccountCoOwnerMutation,
} from '@/api/co-owners'
import { UserAvatar } from '@/components/user-identity-card'

import { getDisplayName, getInitials, getSignInLabel } from './helpers'
import { HOUSEHOLD_PERMISSION_LABEL, HOUSEHOLD_ROLE_DESCRIPTION, HOUSEHOLD_ROLE_LABEL } from './household-roles'

export interface EditMemberSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId: string
  /** The member being edited; null closes the sheet. */
  member: CoOwnerRow | null
  /** Signed-in user's id — drives "(you)" and self-removal copy. */
  viewerUserId: string
  /** The viewer's own grants, from the co-owners response. */
  viewerPermissions: HouseholdPermission[]
  roleCatalog: ListCoOwnersResponse['roleCatalog']
  /**
   * How many primary guardians the household currently has. At 1, the primary
   * on screen is the last one and cannot be demoted.
   */
  primaryGuardianCount: number
}

type View = 'detail' | 'role'

export function EditMemberSheet({
  open,
  onOpenChange,
  accountId,
  member,
  viewerUserId,
  viewerPermissions,
  roleCatalog,
  primaryGuardianCount,
}: EditMemberSheetProps) {
  const [view, setView] = useState<View>('detail')
  const [draftRole, setDraftRole] = useState<HouseholdRole>('GUARDIAN')
  const updateMutation = useUpdateAccountCoOwnerMutation(accountId)
  const removeMutation = useRemoveAccountCoOwnerMutation(accountId)
  const confirm = useDestructiveConfirm()

  // Reset to the detail view whenever a different member is opened, so the
  // sheet never reopens mid-flow on the previous member's role picker.
  useEffect(() => {
    if (open) {
      setView('detail')
      setDraftRole(member?.role ?? 'GUARDIAN')
    }
  }, [open, member?.userId, member?.role])

  if (!member) {
    return null
  }

  const displayName = getDisplayName(member.user)
  const isSelf = member.userId === viewerUserId
  const canManageMembers = viewerPermissions.includes('MANAGE_MEMBERS')
  const canManagePrimary = viewerPermissions.includes('MANAGE_PRIMARY_GUARDIAN')
  const isPrimary = member.role === 'PRIMARY_GUARDIAN'
  // Demoting or removing a primary needs MANAGE_PRIMARY_GUARDIAN, and the last
  // primary is off limits to everyone — nobody could grant the role back.
  const isLastPrimary = isPrimary && primaryGuardianCount <= 1
  const canEditRole = canManageMembers && (!isPrimary || canManagePrimary) && !isLastPrimary
  // The billing anchor stays un-removable regardless of role.
  const canRemove = (canManageMembers || isSelf) && !member.isOwner && !isLastPrimary

  const selectableRoles = roleCatalog.filter((entry) => entry.role !== 'PRIMARY_GUARDIAN' || canManagePrimary)

  async function handleSaveRole() {
    if (!member || draftRole === member.role) {
      setView('detail')
      return
    }
    if (draftRole === 'PRIMARY_GUARDIAN') {
      const confirmed = await confirm({
        title: `Make ${displayName} a primary guardian?`,
        body: `${displayName} will be able to do everything you can, including making other people primary guardians. Your own role doesn't change, and billing emails keep going to the same person.`,
        confirmLabel: 'Make primary guardian',
      })
      if (!confirmed) {
        return
      }
    }
    try {
      await updateMutation.mutateAsync({ userId: member.userId, role: draftRole })
      toast.success(`${displayName} is now ${HOUSEHOLD_ROLE_LABEL[draftRole].toLowerCase()}`)
      onOpenChange(false)
    } catch (err) {
      toast.error("Couldn't change role", {
        description: err instanceof Error ? err.message : 'Please try again.',
      })
    }
  }

  async function handleDelete() {
    if (!member) {
      return
    }
    const confirmed = await confirm({
      title: isSelf ? 'Leave this household?' : `Remove ${displayName}?`,
      body: isSelf
        ? 'You will lose access to this household. Any guardian can invite you back later.'
        : 'They lose access immediately. Their previous orders stay on the household.',
      confirmLabel: isSelf ? 'Leave' : 'Remove',
    })
    if (!confirmed) {
      return
    }
    try {
      await removeMutation.mutateAsync(member.userId)
      toast.success(isSelf ? 'You left this household' : `${displayName} removed`)
      onOpenChange(false)
    } catch (err) {
      toast.error("Couldn't remove member", {
        description: err instanceof Error ? err.message : 'Please try again.',
      })
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        {view === 'detail' ? (
          <>
            <SheetHeader className="border-b px-6 py-4">
              <SheetTitle>Edit member</SheetTitle>
              <SheetDescription>What {isSelf ? 'you' : displayName} can do on this household.</SheetDescription>
            </SheetHeader>

            <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 py-5">
              <div className="flex items-center gap-3 rounded-lg border border-border px-3 py-3">
                <UserAvatar
                  name={displayName}
                  initials={getInitials(member.user)}
                  avatarUri={member.user.avatarUri}
                  avatarColor={member.user.avatarColor}
                  size="md"
                  className="h-10 w-10"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground">
                    {displayName}
                    {isSelf ? <span className="ml-1.5 text-sm text-muted-foreground">(you)</span> : null}
                  </p>
                  <p className="break-all text-sm text-muted-foreground">{getSignInLabel(member.user)}</p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-foreground">Role</p>
                {canEditRole ? (
                  <button
                    type="button"
                    onClick={() => setView('role')}
                    className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5 text-left transition-colors hover:border-foreground/30">
                    <span className="text-sm font-medium text-foreground">
                      {member.role ? HOUSEHOLD_ROLE_LABEL[member.role] : 'Guardian'}
                    </span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                  </button>
                ) : (
                  <div className="rounded-lg border border-border bg-muted/20 px-3 py-2.5">
                    <p className="text-sm font-medium text-foreground">
                      {member.role ? HOUSEHOLD_ROLE_LABEL[member.role] : 'Guardian'}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {isLastPrimary
                        ? 'A household needs at least one primary guardian. Make someone else primary to change this.'
                        : isPrimary
                          ? 'Only a primary guardian can change who else is primary.'
                          : 'Only a guardian on this household can change roles.'}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-foreground">Permissions</p>
                <PermissionList permissions={member.permissions} />
              </div>
            </div>

            {canRemove ? (
              <div className="border-t px-6 py-4">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  disabled={removeMutation.isPending}
                  onClick={() => {
                    void handleDelete()
                  }}>
                  {isSelf ? 'Leave household' : 'Delete member'}
                </Button>
              </div>
            ) : null}
          </>
        ) : (
          <form
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(e) => {
              e.preventDefault()
              void handleSaveRole()
            }}>
            <SheetHeader className="border-b px-6 py-4">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Back to member details"
                  onClick={() => setView('detail')}>
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                </Button>
                <SheetTitle>Edit role</SheetTitle>
              </div>
              <SheetDescription>Each role grants the permissions listed under it.</SheetDescription>
            </SheetHeader>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              <RadioGroup
                value={draftRole}
                onValueChange={(value) => setDraftRole(value as HouseholdRole)}
                className="flex flex-col gap-3">
                {selectableRoles.map((entry) => (
                  <Label
                    key={entry.role}
                    htmlFor={`member-role-${entry.role}`}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                    <RadioGroupItem id={`member-role-${entry.role}`} value={entry.role} className="mt-0.5" />
                    <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                      <span className="text-sm font-medium text-foreground">{HOUSEHOLD_ROLE_LABEL[entry.role]}</span>
                      <span className="text-xs text-muted-foreground">{HOUSEHOLD_ROLE_DESCRIPTION[entry.role]}</span>
                      <PermissionList permissions={entry.permissions} compact />
                    </span>
                  </Label>
                ))}
              </RadioGroup>
              {draftRole === 'PRIMARY_GUARDIAN' && member.role !== 'PRIMARY_GUARDIAN' ? (
                <p className="mt-3 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-foreground dark:border-amber-500/40 dark:bg-amber-950/30">
                  A household can have more than one primary guardian. {displayName} will be able to make other people
                  primary too. Your own role stays the same, and billing emails keep going to the same person.
                </p>
              ) : null}
            </div>

            <div className="flex items-center justify-end border-t px-6 py-4">
              <Button type="submit" disabled={updateMutation.isPending || draftRole === member.role}>
                {updateMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
  )
}

/** Read-only capability list. `compact` is the denser variant used in role cards. */
function PermissionList({ permissions, compact = false }: { permissions: HouseholdPermission[]; compact?: boolean }) {
  if (permissions.length === 0) {
    return <p className="text-xs text-muted-foreground">No permissions on this household.</p>
  }
  return (
    <ul className={compact ? 'flex flex-col gap-1' : 'flex flex-col gap-1.5'} role="list">
      {permissions.map((permission) => (
        <li
          key={permission}
          className={`flex items-center gap-2 ${compact ? 'text-xs' : 'text-sm'} text-muted-foreground`}>
          <Check className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
          {HOUSEHOLD_PERMISSION_LABEL[permission]}
        </li>
      ))}
    </ul>
  )
}
